import type { Recipe } from '@recipes/contracts';
import { api } from '@/lib/api';
import { offlineDb, type OfflineImage, type OfflineRecipe } from './db';
import { offlineImageKey, type OfflineImageVariant } from './image-url';

export interface OfflineUsage {
  recipeCount: number;
  imageCount: number;
  imageBytes: number;
  /** navigator.storage.estimate() when available. */
  quotaBytes: number | null;
  usedBytes: number | null;
}

interface ImageTarget {
  assetId: string;
  variant: OfflineImageVariant;
  url: string;
}

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** Collect the medium + thumbnail image URLs of a recipe (cover, gallery, step images). */
export function offlineImageTargets(recipe: Recipe): ImageTarget[] {
  const targets: ImageTarget[] = [];
  const media = [recipe.cover, ...recipe.gallery].filter((m) => m !== null);
  for (const m of media) {
    targets.push({ assetId: m.assetId, variant: 'medium', url: m.url });
    if (m.thumbnailUrl) targets.push({ assetId: m.assetId, variant: 'thumb', url: m.thumbnailUrl });
  }
  for (const section of recipe.stepSections) {
    for (const step of section.steps) {
      if (step.imageAssetId && step.imageUrl)
        targets.push({ assetId: step.imageAssetId, variant: 'medium', url: step.imageUrl });
    }
  }
  return targets;
}

async function fetchImageBlob(url: string): Promise<Blob | null> {
  try {
    const res = await fetch(url, { credentials: 'omit', mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return blob.size > 0 ? blob : null;
  } catch {
    return null;
  }
}

async function storeImages(recipe: Recipe): Promise<number> {
  let stored = 0;
  const targets = offlineImageTargets(recipe);
  await Promise.all(
    targets.map(async (target) => {
      const key = offlineImageKey(target.assetId, target.variant);
      if (await offlineDb.offlineImages.get(key)) {
        stored += 1;
        return;
      }
      const blob = await fetchImageBlob(target.url);
      if (!blob) return;
      const image: OfflineImage = {
        key,
        assetId: target.assetId,
        blob,
        mimeType: blob.type || 'image/jpeg',
        savedAt: Date.now(),
      };
      await offlineDb.offlineImages.put(image);
      stored += 1;
    }),
  );
  return stored;
}

async function pruneOrphanImages(): Promise<void> {
  const recipes = await offlineDb.offlineRecipes.toArray();
  const referenced = new Set<string>();
  for (const r of recipes) for (const t of offlineImageTargets(r.recipe)) referenced.add(t.assetId);
  const images = await offlineDb.offlineImages.toArray();
  const orphaned = images.filter((i) => !referenced.has(i.assetId)).map((i) => i.key);
  if (orphaned.length) await offlineDb.offlineImages.bulkDelete(orphaned);
}

/** How deep base recipes of base recipes are followed when a recipe is stored. */
const MAX_LINK_DEPTH = 3;

/** The base recipes a recipe links to, from ingredient lines and steps. */
function linkedRecipeIds(recipe: Recipe): string[] {
  return [
    ...new Set([
      ...recipe.ingredientSections.flatMap((s) =>
        s.ingredients.flatMap((i) => (i.linkedRecipe ? [i.linkedRecipe.id] : [])),
      ),
      ...recipe.stepSections.flatMap((s) =>
        s.steps.flatMap((step) => (step.linkedRecipes ?? []).map((link) => link.id)),
      ),
    ]),
  ].filter((id) => id !== recipe.id);
}

/**
 * Explicit offline downloads (spec 15): stores the normalized recipe JSON and its resized
 * images in IndexedDB. Nothing is stored implicitly.
 */
export const offlineManager = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  async isDownloaded(recipeId: string): Promise<boolean> {
    return (await offlineDb.offlineRecipes.get(recipeId)) !== undefined;
  },

  async getRecipe(recipeId: string): Promise<OfflineRecipe | undefined> {
    return offlineDb.offlineRecipes.get(recipeId);
  },

  /**
   * Store a recipe (fetching it when only the id is known) plus its images – and the base
   * recipes it links to, so that they open in the kitchen without a connection too.
   */
  async downloadRecipe(
    recipeOrId: Recipe | string,
    viaCollection?: string,
    viaRecipe?: { id: string; depth: number },
  ): Promise<OfflineRecipe> {
    const recipe = typeof recipeOrId === 'string' ? await api.recipes.get(recipeOrId) : recipeOrId;
    const existing = await offlineDb.offlineRecipes.get(recipe.id);
    const viaCollections = new Set(existing?.viaCollections ?? []);
    if (viaCollection) viaCollections.add(viaCollection);
    const viaRecipes = new Set(existing?.viaRecipes ?? []);
    if (viaRecipe) viaRecipes.add(viaRecipe.id);
    const entry: OfflineRecipe = {
      id: recipe.id,
      recipe,
      savedAt: Date.now(),
      viaCollections: [...viaCollections],
      viaRecipes: [...viaRecipes],
      // Entries from before base recipes existed were all asked for.
      explicit: (!viaCollection && !viaRecipe) || (existing ? existing.explicit !== false : false),
    };
    await offlineDb.offlineRecipes.put(entry);
    await storeImages(recipe);
    const depth = (viaRecipe?.depth ?? 0) + 1;
    if (depth <= MAX_LINK_DEPTH) {
      for (const linkedId of linkedRecipeIds(recipe)) {
        const stored = await offlineDb.offlineRecipes.get(linkedId);
        // Already here through this recipe: nothing to do (and no way to go round in circles).
        if (stored?.viaRecipes?.includes(recipe.id)) continue;
        try {
          await offlineManager.downloadRecipe(linkedId, undefined, { id: recipe.id, depth });
        } catch {
          // the main recipe is stored; a base recipe that cannot be fetched is not worth failing for
        }
      }
    }
    notify();
    return entry;
  },

  /** Download every recipe of a collection; returns the number of stored recipes. */
  async downloadCollection(collectionId: string): Promise<number> {
    const detail = await api.collections.get(collectionId);
    let count = 0;
    for (const summary of detail.recipes) {
      try {
        await offlineManager.downloadRecipe(summary.id, collectionId);
        count += 1;
      } catch {
        // continue with the remaining recipes; the caller reports partial success
      }
    }
    return count;
  },

  /** Refresh a stored copy with the latest server version (if reachable). */
  async refresh(recipeId: string): Promise<void> {
    const existing = await offlineDb.offlineRecipes.get(recipeId);
    if (!existing) return;
    const recipe = await api.recipes.get(recipeId);
    await offlineDb.offlineRecipes.put({ ...existing, recipe, savedAt: Date.now() });
    await storeImages(recipe);
    notify();
  },

  async remove(recipeId: string): Promise<void> {
    // Base recipes that are only here because of removed recipes go with them.
    const pending = [recipeId];
    const removed = new Set<string>();
    while (pending.length) {
      const id = pending.pop()!;
      if (removed.has(id)) continue;
      removed.add(id);
      await offlineDb.offlineRecipes.delete(id);
      const dependents = (await offlineDb.offlineRecipes.toArray()).filter((entry) =>
        entry.viaRecipes?.includes(id),
      );
      for (const entry of dependents) {
        const viaRecipes = (entry.viaRecipes ?? []).filter((via) => via !== id);
        const needed =
          entry.explicit !== false || viaRecipes.length > 0 || entry.viaCollections.length > 0;
        if (needed) await offlineDb.offlineRecipes.put({ ...entry, viaRecipes });
        else pending.push(entry.id);
      }
    }
    await pruneOrphanImages();
    notify();
  },

  async removeCollection(collectionId: string): Promise<void> {
    const entries = await offlineDb.offlineRecipes
      .where('viaCollections')
      .equals(collectionId)
      .toArray();
    for (const entry of entries) {
      const remaining = entry.viaCollections.filter((c) => c !== collectionId);
      if (remaining.length === 0) await offlineDb.offlineRecipes.delete(entry.id);
      else await offlineDb.offlineRecipes.put({ ...entry, viaCollections: remaining });
    }
    await pruneOrphanImages();
    notify();
  },

  async removeAll(): Promise<void> {
    await offlineDb.offlineRecipes.clear();
    await offlineDb.offlineImages.clear();
    notify();
  },

  async list(): Promise<OfflineRecipe[]> {
    return offlineDb.offlineRecipes.orderBy('savedAt').reverse().toArray();
  },

  async usage(): Promise<OfflineUsage> {
    const [recipeCount, images] = await Promise.all([
      offlineDb.offlineRecipes.count(),
      offlineDb.offlineImages.toArray(),
    ]);
    let quotaBytes: number | null = null;
    let usedBytes: number | null = null;
    try {
      if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
        const estimate = await navigator.storage.estimate();
        quotaBytes = estimate.quota ?? null;
        usedBytes = estimate.usage ?? null;
      }
    } catch {
      // unsupported
    }
    return {
      recipeCount,
      imageCount: images.length,
      imageBytes: images.reduce((sum, i) => sum + i.blob.size, 0),
      quotaBytes,
      usedBytes,
    };
  },
};
